// ErrorView — terminal failure state. Shows which phase failed, raw output,
// and offers contextual recovery actions.

import { Alert, Box, Button, Group, Stack, Text, ThemeIcon } from '@mantine/core'
import { IconAlertHexagon, IconRefresh } from '@tabler/icons-react'
import { motion } from 'framer-motion'
import { useMemo } from 'react'
import { HeroCardiograph } from './HeroCardiograph'
import { OperationLogsViewer } from './OperationLogsViewer'
import { PhaseTimeline } from './PhaseTimeline'
import { COLOR, MOTION, SURFACE } from './tokens'
import { PHASE_LABELS } from './types'
import type { DockerOperation, PhaseEntry } from './types'

interface Props {
  operation: DockerOperation
  onRetry?: () => void
  onClose: () => void
}

export function ErrorView({ operation, onRetry, onClose }: Props) {
  const failed: PhaseEntry | undefined = useMemo(() => {
    return [...operation.phases].reverse().find(p => p.status === 'failed')
  }, [operation])

  const cancelled = operation.status === 'cancelled'

  return (
    <motion.div
      key="error"
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={MOTION.viewSwap}
    >
      <Stack gap="md">
        <Box
          p={16}
          style={{
            background: cancelled ? COLOR.muted : COLOR.danger,
            color: '#fff',
            borderRadius: 8,
            overflow: 'hidden',
          }}
        >
          <Group gap={12} align="center">
            <ThemeIcon
              size={36}
              radius="xl"
              variant="filled"
              color={cancelled ? 'gray' : 'red'}
              style={{ background: 'rgba(255,255,255,0.18)' }}
            >
              <IconAlertHexagon size={20} />
            </ThemeIcon>
            <Stack gap={2}>
              <Text fw={700} size="lg" style={{ fontFamily: '"Barlow", sans-serif' }}>
                {cancelled ? 'Restart cancelado' : 'Restart falhou'}
              </Text>
              <Text size="xs" style={{ opacity: 0.85 }}>
                {operation.container_name.replace(/^\//, '')}
                {failed ? ` · falhou em ${PHASE_LABELS[failed.name]}` : ''}
              </Text>
            </Stack>
          </Group>
          <Box mt={12}>
            <HeroCardiograph state={cancelled ? 'stopped' : 'failed'} height={56} />
          </Box>
        </Box>

        {failed?.error && (
          <Alert color="red" variant="light" icon={<IconAlertHexagon size={16} />}>
            {failed.error}
          </Alert>
        )}

        <Box
          p={12}
          style={{
            background: SURFACE.card,
            border: `1px solid ${SURFACE.cardBorder}`,
            borderRadius: 8,
          }}
        >
          <PhaseTimeline operation={operation} />
        </Box>

        <OperationLogsViewer operation={operation} defaultOpen />

        <Group justify="flex-end">
          {onRetry && !cancelled && (
            <Button
              variant="light"
              color="orange"
              leftSection={<IconRefresh size={14} />}
              onClick={onRetry}
            >
              Tentar novamente
            </Button>
          )}
          <Button variant="default" onClick={onClose}>
            Fechar
          </Button>
        </Group>
      </Stack>
    </motion.div>
  )
}
